import React from 'react';

interface FormSectionProps {
  title: string;
  children: React.ReactNode;
  isSubSection?: boolean;
  columns?: 1 | 2 | 3 | 4;
  className?: string; // Additional classes for the container
  marginBottom?: string; // Optional override for margin bottom
  gapY?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12 | string; // Standard tailwind gap values or custom string
}

/**
 * A reusable container for form sections with a styled title.
 * 
 * @param title - The section title
 * @param children - The form fields or content to be placed in the grid
 * @param isSubSection - If true, uses h3 style without the blue line. If false, uses h2 with blue line.
 * @param columns - Number of columns in the grid layout (1, 2, 3, or 4)
 * @param className - Optional additional CSS classes
 * @param marginBottom - Optional custom margin bottom (e.g., "mb-10")
 * @param gapY - Optional custom vertical gap for the grid
 */
const FormSection: React.FC<FormSectionProps> = ({ 
  title, 
  children, 
  isSubSection = false, 
  columns = 3,
  className = "",
  marginBottom,
  gapY
}) => {
  // Determine grid columns class
  const gridColsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 xl:grid-cols-4',
  }[columns] || 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3';

  // Determine vertical gap class
  const gapYClass = gapY !== undefined ? {
      0: 'gap-y-0',
      1: 'gap-y-1',
      2: 'gap-y-2',
      3: 'gap-y-3',
      4: 'gap-y-4',
      5: 'gap-y-5',
      6: 'gap-y-6',
      8: 'gap-y-8',
      10: 'gap-y-10',
      12: 'gap-y-12'
  }[gapY] : 'gap-y-[28px] md:gap-y-[37px]';

  const verticalGapClass = gapYClass || 'gap-y-[28px] md:gap-y-[37px]';

  if (isSubSection) {
    return (
      <div className={`flex flex-col w-full ${marginBottom || 'mb-5 md:mb-8'} ${className}`}>
        <h3 className="font-['Lexend',sans-serif] text-[14px] lg:text-[18px] font-medium text-[#1F2A37] mb-4 mt-2 leading-[29px]">
          {title}
        </h3>
        <div className={`grid ${gridColsClass} gap-x-[60px] ${verticalGapClass}`}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col w-full h-full ${marginBottom || 'mb-[24px] md:mb-[37px]'} ${className}`}>
      <div className="flex items-center gap-4 mb-[28px] lg:mb-[37px]">
        <h2 className="font-['Lexend',sans-serif] text-[14px] md:text-[20px] font-medium text-[#1F2A37] m-0 leading-[29px]">
          {title}
        </h2>
        <div className="w-[30px] md:w-[46px] h-0 border-t-[1.5px] md:border-t-[2px] border-[#6FB6F2]"></div>
      </div>
      <div className={`grid ${gridColsClass} gap-x-[60px] ${verticalGapClass}`}>
        {children}
      </div>
    </div>
  );
};

export default FormSection;
